import { uniq } from 'lodash';
import axios from 'axios';

import { RESET_ALL as RESET_ON_LOGOUT } from 'redux-store/reducers/common-reducer';

import { createReducer } from 'utils/redux';
import { API, get } from 'utils/api';
import { logError } from 'utils/log';
import { snakeCaseKeys } from 'utils/keys';
import { Querystring } from 'utils/url';
import { TYPES, TYPE_ACCEPTED, TYPE_FINISHED, TYPE_PENDING, TYPE_REJECTED } from 'utils/influencer-subcampaigns';
import { SUBCAMPAIGN_STATE_ACCEPTED, SUBCAMPAIGN_STATE_PENDING, SUBCAMPAIGN_STATE_REVOKED } from 'utils/subcampaign';
import { CAMPAIGN_STATE_ACTIVE, CAMPAIGN_STATE_INACTIVE, CAMPAIGN_STATE_PREPARATION } from 'utils/campaign';

const RECEIVE = 'influencerSubcampaigns/RECEIVE';
const RESET = 'influencerSubcampaigns/RESET';
const LOADING_STATE_CHANGED = 'influencerSubcampaigns/LOADING_STATE_CHANGED';
const COUNTS_RECEIVED = 'influencerSubcampaigns/COUNTS_RECEIVED';

const { INFLUENCERS, BRANDS, CAMPAIGNS } = API.END_POINTS;

import type { Action, Subcampaign, ThunkAction } from 'types';

const ITEMS_PER_PAGE = 10;
const SORT_BY_CAMPAIGN_START_DATE = 'campaign_start_date.desc';
const SORT_BY_CAMPAIGN_END_DATE = 'campaign_end_date.desc';

const initialState = {
  counts: {
    [TYPE_PENDING]: 0,
    [TYPE_ACCEPTED]: 0,
    [TYPE_FINISHED]: 0,
    [TYPE_REJECTED]: 0
  },
  hasCampaigns: false,
  list: [],
  pagination: {},
  isLoading: true
};

function loadingStateChanged(isLoading: boolean): Action {
  return {
    type: LOADING_STATE_CHANGED,
    payload: {
      isLoading
    }
  };
}

function receiveSubcampaigns(data): Action {
  return {
    type: RECEIVE,
    payload: {
      data
    }
  };
}

function receiveCounts(counts): Action {
  return {
    type: COUNTS_RECEIVED,
    payload: {
      counts
    }
  };
}

export function resetInfluencerSubcampaigns(): Action {
  return {
    type: RESET
  };
}

function getBrandProfilesBulk(brandProfileIds) {
  if (brandProfileIds.length === 0) {
    return Promise.resolve([]);
  }
  return get(BRANDS.getBrandProfilesInBulk(brandProfileIds))
    .then((response: any) => response.data)
    .catch(() => []);
}

function getCampaignsBulk(campaignIds) {
  if (campaignIds.length === 0) {
    return Promise.resolve([]);
  }
  return get(`${CAMPAIGNS.getCampaignsBulk()}?ids=${campaignIds.join(',')}&include=main_image`)
    .then((response: any) => response.data)
    .catch(() => []);
}

function getQueryParams(activeSubcampaignType: string) {
  if (activeSubcampaignType === TYPE_PENDING) {
    return {
      states: SUBCAMPAIGN_STATE_PENDING,
      campaignStates: [CAMPAIGN_STATE_PREPARATION, CAMPAIGN_STATE_ACTIVE].join(','),
      sortBy: SORT_BY_CAMPAIGN_START_DATE
    };
  }

  if (activeSubcampaignType === TYPE_ACCEPTED) {
    return {
      states: SUBCAMPAIGN_STATE_ACCEPTED,
      campaignStates: [CAMPAIGN_STATE_PREPARATION, CAMPAIGN_STATE_ACTIVE].join(','),
      sortBy: SORT_BY_CAMPAIGN_START_DATE
    };
  }

  if (activeSubcampaignType === TYPE_FINISHED) {
    return {
      states: SUBCAMPAIGN_STATE_ACCEPTED,
      campaignStates: CAMPAIGN_STATE_INACTIVE,
      sortBy: SORT_BY_CAMPAIGN_END_DATE
    };
  }

  if (activeSubcampaignType === TYPE_REJECTED) {
    return {
      states: SUBCAMPAIGN_STATE_REVOKED,
      campaignStates: [CAMPAIGN_STATE_PREPARATION, CAMPAIGN_STATE_ACTIVE, CAMPAIGN_STATE_INACTIVE].join(','),
      sortBy: SORT_BY_CAMPAIGN_START_DATE
    };
  }

  return {};
}

async function fetchInfluencerSubcampaignsCountByType(influencerProfileId: number, activeSubcampaignType: string): Promise<number> {
  const params = snakeCaseKeys({
    ...getQueryParams(activeSubcampaignType),
    itemsPerPage: 1,
    page: 1
  });

  const queryString = Querystring.objectToString(params);
  const url = INFLUENCERS.getInfluencerSubcampaigns(influencerProfileId, queryString);

  try {
    const response = (await get(url)) as { data: { meta: { itemsTotal: number } } };

    return response.data.meta.itemsTotal;
  } catch (error) {
    logError(error);
  }

  return 0;
}

export function fetchInfluencerSubcampaignsCounts(influencerProfileId: number): ThunkAction {
  return async (dispatch) => {
    try {
      const countsList = await Promise.all(TYPES.map(type => fetchInfluencerSubcampaignsCountByType(influencerProfileId, type)));
      const counts = TYPES.reduce((map, type, index) => {
        map[type] = countsList[index]; // eslint-disable-line no-param-reassign
        return map;
      }, {});

      dispatch(receiveCounts(counts));

      return counts;
    } catch (error) {
      logError(error);
      dispatch(receiveCounts(initialState.counts));
    }

    return initialState.counts;
  };
}

let fetchInfluencerSubcampaignsCancelToken;

export function fetchInfluencerSubcampaigns(influencerProfileId: number, activeSubcampaignType: string, page: number): ThunkAction {
  const params = snakeCaseKeys({
    ...getQueryParams(activeSubcampaignType),
    itemsPerPage: ITEMS_PER_PAGE,
    page: page || 1
  });

  const queryString = Querystring.objectToString(params);
  const url = INFLUENCERS.getInfluencerSubcampaigns(influencerProfileId, queryString);

  return (dispatch) => {
    dispatch(loadingStateChanged(true));

    if (fetchInfluencerSubcampaignsCancelToken) {
      fetchInfluencerSubcampaignsCancelToken.cancel();
    }
    fetchInfluencerSubcampaignsCancelToken = axios.CancelToken.source();

    return get(url, { cancelToken: fetchInfluencerSubcampaignsCancelToken.token })
      .then(async (response: any) => {
        const subcampaigns: Array<Subcampaign> = response.data.data;
        const brandIds = uniq(subcampaigns.map(s => s.campaign.brandProfileId));

        const brands = await getBrandProfilesBulk(brandIds);

        const brandsMap = {};

        brands.forEach(brand => {
          brandsMap[brand.id] = brand;
        });

        const campaignIds = subcampaigns.map(subcampaign => subcampaign.campaign.id);

        const campaigns = await getCampaignsBulk(campaignIds);

        const campaignsMap = {};

        campaigns.forEach(campaign => {
          campaignsMap[campaign.id] = campaign;
        });

        const enrichedSubcampaigns = subcampaigns.map(subcampaign => ({
          ...subcampaign,
          campaign: {
            ...subcampaign.campaign,
            mainImage: campaignsMap[subcampaign.campaign.id].mainImage || null,
            brandProfile: brandsMap[subcampaign.campaign.brandProfileId]
          }
        }));

        dispatch(receiveSubcampaigns({
          ...response.data,
          data: enrichedSubcampaigns
        }));
        dispatch(loadingStateChanged(false));
      })
      .catch((error) => {
        logError(error);
        if (axios.isCancel(error)) {
          return;
        }

        dispatch(loadingStateChanged(false));
        dispatch(receiveSubcampaigns({
          data: []
        }));
      });
  };
}

export default createReducer<any, any>('influencerSubcampaigns', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RESET]: () => initialState,
  [RECEIVE]: (state, { data }) => ({
    ...state,
    list: data.data,
    pagination: {
      ...data.meta,
      links: data.links
    }
  }),
  [COUNTS_RECEIVED]: (state, { counts }) => ({
    ...state,
    counts: {
      ...state.counts,
      ...counts
    },
    hasCampaigns: TYPES.some(type => counts[type] > 0)
  }),
  [LOADING_STATE_CHANGED]: (state, { isLoading }) => ({
    ...state,
    isLoading
  })
});
